'use client';

import { useEffect, useState } from 'react';
import { useStore } from '@/lib/store';
import {
  destroyVault,
  getSnapshot,
  initialize,
  loadFromDisk,
  lock,
  removeKalshiKey,
  removePolymarketKey,
  setKalshiKey,
  setPolymarketKey,
  signKalshiRequest,
  subscribeVault,
} from '@/lib/vault';
import { derivePolymarketL2, testPolymarketBalance } from '@/lib/vault/polymarket-actions';
import {
  getApprovalView,
  isFullyApproved,
  refreshApprovalState,
  runApprovals,
  subscribeApprovals,
  type ApprovalView,
} from '@/lib/polymarket/approvals';
import { formatUsdc } from '@/lib/polymarket/polygon';
import { send } from '@/lib/ws-client';
import { cn } from '@/lib/cn';
import { updateRiskConfig, useRiskConfig } from '@/lib/risk/limits';

const KALSHI_BALANCE_PATH = '/portfolio/balance';

export default function ProfilePage(): JSX.Element {
  const vault = useStore((s) => s.vault);
  const connected = useStore((s) => s.connected);
  const setVaultSnapshot = useStore((s) => s.setVaultSnapshot);
  const startVaultTest = useStore((s) => s.startVaultTest);
  const setVaultTestResult = useStore((s) => s.setVaultTestResult);
  const [bootLoaded, setBootLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadFromDisk()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setBootLoaded(true);
      });
    const unsub = subscribeVault((snap) => setVaultSnapshot(snap));
    setVaultSnapshot(getSnapshot());
    return () => {
      cancelled = true;
      unsub();
    };
  }, [setVaultSnapshot]);

  async function runBalanceTest(): Promise<void> {
    const headers = await signKalshiRequest('GET', KALSHI_BALANCE_PATH);
    if (!headers) {
      setVaultTestResult({ ok: false, reason: 'no kalshi key in vault' });
      return;
    }
    const ref = crypto.randomUUID();
    startVaultTest();
    const sent = send({ t: 'vault_test_kalshi', ref, headers });
    if (!sent) {
      setVaultTestResult({ ok: false, reason: 'executor not connected' });
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-4 flex flex-col gap-6">
      <header>
        <h1 className="text-lg font-semibold text-text">Profile</h1>
        <p className="text-xs text-muted mt-1">
          Set up the local key vault that signs your venue requests. Keys are
          encrypted with your passphrase and stored only in this browser — the
          executor never sees them.
        </p>
      </header>

      {!bootLoaded ? (
        <div className="text-sm text-muted">Loading vault…</div>
      ) : vault.phase === 'absent' ? (
        <VaultSetup />
      ) : vault.phase === 'locked' ? (
        <VaultUnlock />
      ) : (
        <VaultUnlocked
          balance={vault.kalshiBalance}
          testInFlight={vault.testInFlight}
          testReason={vault.testReason}
          testTs={vault.testTs}
          hasKalshi={vault.hasKalshi}
          vaultId={vault.vaultId}
          connected={connected}
          onTest={runBalanceTest}
        />
      )}
    </div>
  );
}

function VaultSetup(): JSX.Element {
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    if (pass.length < 8) {
      setErr('passphrase must be at least 8 characters');
      return;
    }
    if (pass !== pass2) {
      setErr('passphrases do not match');
      return;
    }
    setBusy(true);
    try {
      await initialize(pass);
    } catch (e2) {
      setErr(String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="rounded-lg border border-border bg-panel p-4 flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-text">Set up your vault</h2>
      <p className="text-xs text-muted">
        Pick a passphrase. You&apos;ll need it every time you open this browser to
        trade. If you forget it, the keys can&apos;t be recovered — you&apos;ll have to
        reset the vault and paste your venue keys again.
      </p>
      <PassphraseInput value={pass} onChange={setPass} label="Passphrase" autoFocus />
      <PassphraseInput value={pass2} onChange={setPass2} label="Confirm passphrase" />
      {err ? <div className="text-xs text-err">{err}</div> : null}
      <button
        type="submit"
        disabled={busy || !pass || !pass2}
        className="self-start rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20 disabled:opacity-50"
      >
        {busy ? 'Creating…' : 'Create vault'}
      </button>
    </form>
  );
}

function VaultUnlock(): JSX.Element {
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { unlock } = await import('@/lib/vault');
      await unlock(pass);
    } catch (e2) {
      setErr(String(e2));
    } finally {
      setBusy(false);
    }
  }

  async function onReset(): Promise<void> {
    if (!confirm('Erase the local vault and all stored keys? This cannot be undone.')) return;
    await destroyVault();
  }

  return (
    <form onSubmit={onSubmit} className="rounded-lg border border-border bg-panel p-4 flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-text">Unlock your vault</h2>
      <PassphraseInput value={pass} onChange={setPass} label="Passphrase" autoFocus />
      {err ? <div className="text-xs text-err">{err}</div> : null}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || !pass}
          className="rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20 disabled:opacity-50"
        >
          {busy ? 'Unlocking…' : 'Unlock'}
        </button>
        <button
          type="button"
          onClick={onReset}
          className="text-[11px] text-muted hover:text-err"
        >
          forgot passphrase — reset vault
        </button>
      </div>
    </form>
  );
}

interface UnlockedProps {
  balance: number | null;
  testInFlight: boolean;
  testReason: string | null;
  testTs: number | null;
  hasKalshi: boolean;
  vaultId: string | null;
  connected: boolean;
  onTest: () => void;
}

function VaultUnlocked(props: UnlockedProps): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-ok/40 bg-ok/5 p-3 text-xs text-ok flex items-center justify-between">
        <span>Vault unlocked</span>
        <div className="flex items-center gap-3 text-muted">
          {props.vaultId ? (
            <span className="font-mono text-[10px]">id {props.vaultId.slice(0, 8)}…</span>
          ) : null}
          <button
            type="button"
            onClick={() => lock()}
            className="text-muted hover:text-text"
          >
            lock
          </button>
        </div>
      </div>

      <RiskSection />

      <KalshiSection
        hasKey={props.hasKalshi}
        balance={props.balance}
        testInFlight={props.testInFlight}
        testReason={props.testReason}
        testTs={props.testTs}
        connected={props.connected}
        onTest={props.onTest}
      />

      <PolymarketSection />

      <div className="text-[11px] text-muted">
        <button
          type="button"
          onClick={async () => {
            if (!confirm('Erase the vault, clear all keys, and start over?')) return;
            await destroyVault();
          }}
          className="hover:text-err"
        >
          reset vault
        </button>
      </div>
    </div>
  );
}

interface KalshiSectionProps {
  hasKey: boolean;
  balance: number | null;
  testInFlight: boolean;
  testReason: string | null;
  testTs: number | null;
  connected: boolean;
  onTest: () => void;
}

function KalshiSection(props: KalshiSectionProps): JSX.Element {
  const [keyId, setKeyId] = useState('');
  const [pem, setPem] = useState('');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  async function onConnect(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setLocalErr(null);
    setBusy(true);
    try {
      await setKalshiKey(keyId, pem);
      setKeyId('');
      setPem('');
      props.onTest();
    } catch (e2) {
      setLocalErr(String(e2));
    } finally {
      setBusy(false);
    }
  }

  async function onDisconnect(): Promise<void> {
    if (!confirm('Remove your Kalshi key from the vault?')) return;
    await removeKalshiKey();
  }

  if (props.hasKey) {
    return (
      <div className="rounded-lg border border-border bg-panel p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text">Kalshi</h2>
          <span className="text-[10px] uppercase tracking-wider text-ok">connected</span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Stat
            label="Balance"
            value={props.balance != null ? formatBalance(props.balance) : '—'}
            tone={props.balance != null ? 'ok' : 'muted'}
          />
          <Stat
            label="Last test"
            value={props.testTs ? `${Math.max(0, Math.floor((Date.now() - props.testTs) / 1000))}s ago` : '—'}
            tone="muted"
          />
        </div>
        {props.testReason ? (
          <div className="text-xs text-err font-mono break-all">{props.testReason}</div>
        ) : null}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={props.onTest}
            disabled={props.testInFlight || !props.connected}
            className="rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20 disabled:opacity-50"
          >
            {props.testInFlight ? 'Testing…' : 'Test connection'}
          </button>
          <button
            type="button"
            onClick={onDisconnect}
            className="rounded border border-border px-3 py-1 text-xs text-muted hover:text-err"
          >
            Disconnect
          </button>
          {!props.connected ? (
            <span className="text-[11px] text-warn">executor offline</span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onConnect} className="rounded-lg border border-border bg-panel p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text">Kalshi</h2>
        <span className="text-[10px] uppercase tracking-wider text-muted">not connected</span>
      </div>
      <p className="text-[11px] text-muted">
        Paste your Kalshi API key id and PKCS#8-encoded RSA private key. They
        stay in this browser, encrypted with your vault passphrase.
      </p>
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-muted">Key ID</span>
        <input
          value={keyId}
          onChange={(e) => setKeyId(e.target.value)}
          placeholder="kal_..."
          className="rounded border border-border bg-bg px-2 py-1 text-sm text-text font-mono outline-none focus:border-accent"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-muted">Private key (PEM, PKCS#8)</span>
        <textarea
          value={pem}
          onChange={(e) => setPem(e.target.value)}
          placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
          rows={8}
          className="rounded border border-border bg-bg px-2 py-1 text-[11px] text-text font-mono outline-none focus:border-accent"
        />
      </label>
      {localErr ? <div className="text-xs text-err font-mono break-all">{localErr}</div> : null}
      <button
        type="submit"
        disabled={busy || !keyId || !pem}
        className="self-start rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Connect Kalshi'}
      </button>
    </form>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: 'ok' | 'muted' }): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-muted">{label}</span>
      <span className={cn('font-mono tabular-nums text-sm', tone === 'ok' ? 'text-text' : 'text-muted')}>
        {value}
      </span>
    </div>
  );
}

function PassphraseInput({
  value,
  onChange,
  label,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  autoFocus?: boolean;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted">{label}</span>
      <input
        type="password"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border bg-bg px-2 py-1 text-sm text-text outline-none focus:border-accent"
      />
    </label>
  );
}

// Kalshi reports balance in cents. Display as USD with 2 decimals.
function formatBalance(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function PolymarketSection(): JSX.Element {
  const vault = useStore((s) => s.vault);
  const connected = useStore((s) => s.connected);
  const [pk, setPk] = useState('');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [approvals, setApprovals] = useState<ApprovalView>(() => getApprovalView());

  // Subscribe to the approvals module (separate from Zustand because the
  // tx hash + step transitions don't need to fan out across the app).
  useEffect(() => subscribeApprovals(setApprovals), []);

  // Refresh approval state when the EOA appears or changes.
  useEffect(() => {
    if (vault.hasPolymarket && vault.polymarketAddress) {
      void refreshApprovalState();
    }
  }, [vault.hasPolymarket, vault.polymarketAddress]);

  async function onConnect(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setLocalErr(null);
    setBusy(true);
    try {
      const address = await setPolymarketKey(pk);
      setPk('');
      const derived = await derivePolymarketL2();
      if (derived.ok) await testPolymarketBalance();
      else setLocalErr(`saved EOA ${address.slice(0, 10)}… but derive failed: ${derived.reason ?? ''}`);
      // Fire-and-forget approval read; the user can act on the result.
      void refreshApprovalState();
    } catch (e2) {
      setLocalErr(String(e2));
    } finally {
      setBusy(false);
    }
  }

  async function onDerive(): Promise<void> {
    setLocalErr(null);
    setBusy(true);
    try {
      const r = await derivePolymarketL2();
      if (!r.ok) setLocalErr(r.reason ?? 'unknown error');
      else await testPolymarketBalance();
    } finally {
      setBusy(false);
    }
  }

  async function onDisconnect(): Promise<void> {
    if (!confirm('Remove your Polymarket key from the vault?')) return;
    await removePolymarketKey();
  }

  if (vault.hasPolymarket) {
    const fullyApproved = isFullyApproved(approvals.state);
    const usdcChain = approvals.balanceUsdc != null ? formatUsdc(approvals.balanceUsdc) : null;
    return (
      <div className="rounded-lg border border-border bg-panel p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text">Polymarket</h2>
          <span
            className={cn(
              'text-[10px] uppercase tracking-wider',
              fullyApproved ? 'text-ok' : vault.hasPolymarketL2 ? 'text-warn' : 'text-warn',
            )}
          >
            {!vault.hasPolymarketL2
              ? 'derive l2 creds'
              : fullyApproved
                ? 'ready'
                : 'approvals pending'}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-muted">EOA</span>
            <span className="font-mono text-[11px] text-text truncate" title={vault.polymarketAddress ?? ''}>
              {vault.polymarketAddress
                ? `${vault.polymarketAddress.slice(0, 6)}…${vault.polymarketAddress.slice(-4)}`
                : '—'}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-muted">USDC.e balance</span>
            <span className="font-mono text-sm text-text">
              {usdcChain != null
                ? `$${usdcChain}`
                : vault.polymarketBalanceUsd != null
                  ? `$${vault.polymarketBalanceUsd.toFixed(2)}`
                  : '—'}
            </span>
          </div>
        </div>
        <ApprovalsPanel approvals={approvals} canRun={vault.hasPolymarket && connected} />
        {vault.polymarketDeriveReason ? (
          <div className="text-xs text-err font-mono break-all">{vault.polymarketDeriveReason}</div>
        ) : null}
        {vault.polymarketTestReason ? (
          <div className="text-xs text-err font-mono break-all">{vault.polymarketTestReason}</div>
        ) : null}
        <div className="flex items-center gap-2 flex-wrap">
          {!vault.hasPolymarketL2 ? (
            <button
              type="button"
              onClick={() => void onDerive()}
              disabled={busy || vault.polymarketDeriveInFlight || !connected}
              className="rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20 disabled:opacity-50"
            >
              {vault.polymarketDeriveInFlight ? 'Deriving…' : 'Derive L2 credentials'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void testPolymarketBalance()}
              disabled={busy || vault.polymarketTestInFlight || !connected}
              className="rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20 disabled:opacity-50"
            >
              {vault.polymarketTestInFlight ? 'Testing…' : 'Test L2 connection'}
            </button>
          )}
          <button
            type="button"
            onClick={() => void onDisconnect()}
            className="rounded border border-border px-3 py-1 text-xs text-muted hover:text-err"
          >
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onConnect} className="rounded-lg border border-border bg-panel p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text">Polymarket</h2>
        <span className="text-[10px] uppercase tracking-wider text-muted">not connected</span>
      </div>
      <p className="text-[11px] text-muted">
        Paste your Polymarket EOA private key (32-byte hex, with or without `0x`).
        It stays in this browser, encrypted with your vault passphrase, and is used
        once to derive a session API key.
      </p>
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-muted">EOA private key</span>
        <input
          type="password"
          value={pk}
          onChange={(e) => setPk(e.target.value)}
          placeholder="0x…"
          className="rounded border border-border bg-bg px-2 py-1 text-sm text-text font-mono outline-none focus:border-accent"
        />
      </label>
      {localErr ? <div className="text-xs text-err font-mono break-all">{localErr}</div> : null}
      <button
        type="submit"
        disabled={busy || !pk || !connected}
        className="self-start rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Connect Polymarket'}
      </button>
    </form>
  );
}

function ApprovalsPanel({
  approvals,
  canRun,
}: {
  approvals: ApprovalView;
  canRun: boolean;
}): JSX.Element {
  const { state, step, reason, lastTxHash, loading } = approvals;
  const ready = isFullyApproved(state);
  const usdcOk = state != null && state.usdcAllowance >= 1_000_000n;
  const ctfOk = state != null && state.ctfApproved;

  const stepLabel: Record<typeof step, string> = {
    idle: '',
    reading: 'Reading approvals…',
    'approving-usdc': 'Sign + broadcast USDC approve…',
    'approving-ctf': 'Sign + broadcast CTF approve…',
    confirming: 'Waiting for Polygon confirmation…',
  };

  return (
    <div className="rounded border border-border bg-panel-2 p-2.5 flex flex-col gap-2">
      <div className="flex items-center justify-between text-[11px]">
        <span className="uppercase tracking-wider text-muted">Polygon approvals</span>
        <span
          className={cn(
            'text-[10px] uppercase tracking-wider',
            ready ? 'text-ok' : usdcOk || ctfOk ? 'text-warn' : 'text-err',
          )}
        >
          {ready ? '✓ ready to trade' : 'incomplete'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <ApprovalRow label="USDC.e → exchange" ok={usdcOk} />
        <ApprovalRow label="CTF approval" ok={ctfOk} />
      </div>
      {step !== 'idle' ? (
        <div className="text-[11px] text-muted font-mono">{stepLabel[step]}</div>
      ) : null}
      {lastTxHash ? (
        <div className="text-[10px] text-muted font-mono break-all">
          tx <a href={`https://polygonscan.com/tx/${lastTxHash}`} target="_blank" rel="noreferrer" className="hover:text-text underline">
            {lastTxHash.slice(0, 12)}…{lastTxHash.slice(-6)}
          </a>
        </div>
      ) : null}
      {reason ? <div className="text-xs text-err font-mono break-all">{reason}</div> : null}
      {!ready ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void runApprovals()}
            disabled={!canRun || loading}
            className="rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20 disabled:opacity-50"
          >
            {loading ? 'Working…' : 'Approve trading'}
          </button>
          <button
            type="button"
            onClick={() => void refreshApprovalState()}
            disabled={loading}
            className="rounded border border-border px-3 py-1 text-xs text-muted hover:text-text disabled:opacity-50"
          >
            Refresh
          </button>
          <span className="text-[10px] text-muted">
            ~$0.05 in MATIC gas total
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => void refreshApprovalState()}
          disabled={loading}
          className="self-start text-[10px] text-muted hover:text-text"
        >
          re-check
        </button>
      )}
    </div>
  );
}

function ApprovalRow({ label, ok }: { label: string; ok: boolean }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span className={cn('text-[11px]', ok ? 'text-ok' : 'text-warn')}>
        {ok ? '✓' : '○'}
      </span>
      <span className={cn(ok ? 'text-muted' : 'text-text')}>{label}</span>
    </div>
  );
}

function RiskSection(): JSX.Element {
  const risk = useRiskConfig();
  const [maxNotional, setMaxNotional] = useState((risk.maxOrderNotionalCents / 100).toFixed(2));
  const [confirmAbove, setConfirmAbove] = useState((risk.confirmAboveCents / 100).toFixed(2));
  const [qsize, setQsize] = useState(String(risk.quickTradeSize));
  const [saved, setSaved] = useState(false);

  function save(e: React.FormEvent): void {
    e.preventDefault();
    const m = Number(maxNotional);
    const c = Number(confirmAbove);
    const q = Number(qsize);
    if (Number.isFinite(m) && m > 0) updateRiskConfig({ maxOrderNotionalCents: Math.round(m * 100) });
    if (Number.isFinite(c) && c >= 0) updateRiskConfig({ confirmAboveCents: Math.round(c * 100) });
    if (Number.isFinite(q) && q > 0) updateRiskConfig({ quickTradeSize: Math.floor(q) });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1500);
  }

  return (
    <form onSubmit={save} className="rounded-lg border border-border bg-panel p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text">Risk limits</h2>
        {saved ? <span className="text-[10px] text-ok">saved</span> : null}
      </div>
      <p className="text-[11px] text-muted">
        Client-enforced caps for this browser. Each order&apos;s notional is
        `price × size × $1`. The hard cap rejects above-limit submits; quick-
        trade uses the configured size when you click a price cell.
      </p>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <label className="flex flex-col gap-1">
          <span className="text-muted">Max notional ($)</span>
          <input
            value={maxNotional}
            onChange={(e) => setMaxNotional(e.target.value)}
            inputMode="decimal"
            className="rounded border border-border bg-bg px-2 py-1 text-sm text-text font-mono outline-none focus:border-accent"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted">Confirm above ($)</span>
          <input
            value={confirmAbove}
            onChange={(e) => setConfirmAbove(e.target.value)}
            inputMode="decimal"
            className="rounded border border-border bg-bg px-2 py-1 text-sm text-text font-mono outline-none focus:border-accent"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted">Quick-trade size</span>
          <input
            value={qsize}
            onChange={(e) => setQsize(e.target.value)}
            inputMode="numeric"
            className="rounded border border-border bg-bg px-2 py-1 text-sm text-text font-mono outline-none focus:border-accent"
          />
        </label>
      </div>
      <button
        type="submit"
        className="self-start rounded border border-accent bg-accent/10 px-3 py-1 text-xs text-accent hover:bg-accent/20"
      >
        Save limits
      </button>
      <p className="text-[10px] text-muted">
        Today&apos;s values: max ${(risk.maxOrderNotionalCents / 100).toFixed(2)} ·
        confirm ${(risk.confirmAboveCents / 100).toFixed(2)} · size {risk.quickTradeSize} contracts.
        Daily-loss-cap arrives in a later release; for now, the hard cap is the safety net.
      </p>
    </form>
  );
}
